// components/customer/ProductCard.tsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Product, ProductVariant } from '../../types';
import { ShoppingCart, Star, Package, ChevronRight } from 'lucide-react';
import { formatPrice } from '../../utils/utilityFunctions';
import { useCart } from '../../contexts/CartContext';
import toast from 'react-hot-toast';
import AddToCartButton from '../common/AddToCartButton';

interface ProductCardProps {
  product: Product;
  viewMode: 'grid' | 'list';
}

const ProductCard: React.FC<ProductCardProps> = ({ product, viewMode }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const navigate = useNavigate();
  const { addItem } = useCart();

  // Calculate available quantity
  const calculateAvailableQuantity = (): number => {
    if (product.total_available_quantity !== undefined) {
      return product.total_available_quantity;
    }
    if (product.variants?.length > 0) {
      return product.variants.reduce((total: number, variant: ProductVariant) => {
        return total + (variant.available_quantity || 0);
      }, 0);
    }
    return 0;
  };

  // Get primary image
  const getPrimaryImage = (): string | null => {
    if (product.variants?.[0]?.images?.[0]?.image_url) {
      return product.variants[0].images[0].image_url;
    }
    if (product.variants?.[0]?.image_url) {
      return product.variants[0].image_url;
    }
    if (product.images?.[0]?.image_url) {
      return product.images[0].image_url;
    }
    return product.image_url || null;
  };

  // Get display price
  const getDisplayPrice = (): number => {
    if (product.variants?.length > 0) {
      const prices = product.variants
        .filter(v => v.is_active)
        .map(v => v.price || product.selling_price)
        .filter(price => typeof price === 'number');
      if (prices.length > 0) return Math.min(...prices);
    }
    return product.selling_price || 0;
  };

  const hasSingleVariant = product.variants?.length === 1;
  const hasMultipleVariants = product.variants && product.variants.length > 1;

  const availableQuantity = calculateAvailableQuantity();
  const isOutOfStock = availableQuantity <= 0;
  const isLowStock = availableQuantity > 0 && availableQuantity <= 5;
  const primaryImage = getPrimaryImage();
  const displayPrice = getDisplayPrice();
  const discountPrice = product.discount_percent
    ? displayPrice - (displayPrice * (product.discount_percent / 100))
    : null;
  const hasVariants = product.variants && product.variants.length > 1;

  // Handle card click
  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on buttons or links inside the card
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a')) {
      return;
    }
    navigate(`/product/${product.id}`);
  };

  // Handle add to cart click

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click

    if (hasMultipleVariants) {
      navigate(`/product/${product.id}`);
      return;
    }

    if (isOutOfStock) return;

    setIsAddingToCart(true);
    try {
      // For single variant products, add directly to cart
      const variant = product.variants?.[0];
      const allImages = product.images?.map(img => img.image_url) || [];
      await addItem({
        product_variant_id: variant?.id || product.id,
        product_id: product.id,
        product_title: product.title,
        product_description: product.description,
        variant_name: variant?.name || 'Default',
        sku: variant?.full_sku || product.sku,
        price: product.selling_price,
        quantity: 1,
        image_url: product.images?.[0]?.image_url,
        vendor_id: product.vendor || null,
        vendor_name: product.vendor_name || '',
        additional_images: allImages,
      });

      // Show success toast
      toast.success(
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          <span>Added <strong>{product.title}</strong> to cart!</span>
        </div>,
        {
          duration: 3000,
          position: 'bottom-right',
          style: {
            background: '#10B981',
            color: 'white',
          },
          icon: '🛒',
        }
      );

    } catch (error) {
      toast.error('Failed to add to cart. Please try again.');
    } finally {
      setIsAddingToCart(false);
    }
  };

  if (viewMode === 'list') {
    return (
      <div
        className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
        onClick={handleCardClick}
      >
        <div className="flex flex-col md:flex-row">
          {/* Image */}
          <div className="relative w-full md:w-48 h-48 md:h-auto bg-gray-50">
            {primaryImage ? (
              <>
                {!imageLoaded && (
                  <div className="absolute inset-0 bg-gray-200 animate-pulse" />
                )}
                <img
                  src={primaryImage}
                  alt={product.title}
                  className={`w-full h-full object-cover ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                  onLoad={() => setImageLoaded(true)}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/placeholder-product.png';
                  }}
                />
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Package className="h-12 w-12 text-gray-300" />
              </div>
            )}

            {/* Badges */}
            <div className="absolute top-2 left-2 flex flex-col gap-1">
              {product.is_featured && (
                <span className="px-2 py-1 bg-blue-600 text-white text-xs font-medium rounded">
                  Featured
                </span>
              )}
              {product.discount_percent && (
                <span className="px-2 py-1 bg-red-600 text-white text-xs font-medium rounded">
                  -{product.discount_percent}%
                </span>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 p-4 md:p-6">
            {/* Title */}
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {product.title}
            </h3>

            {/* Description */}
            <p className="text-gray-600 text-sm mb-4 line-clamp-2">
              {product.description}
            </p>

            {/* Variant Indicator */}
            {hasVariants && (
              <div className="mb-3">
                <span className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-lg">
                  {product.variants?.length} variants available
                  <ChevronRight className="h-4 w-4 ml-1" />
                </span>
              </div>
            )}

            {/* Rating */}
            {product.rating && (
              <div className="flex items-center gap-1 mb-3">
                <Star className="h-4 w-4 text-amber-400 fill-current" />
                <span className="text-sm font-medium text-gray-900">{product.rating}</span>
                <span className="text-xs text-gray-500">({product.view_count || 0})</span>
              </div>
            )}

            {/* Stock & Price Row */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="space-y-2">
                {/* Price */}
                <div className="flex items-baseline gap-2">
                  <span className="text-xl font-bold text-gray-900">
                    {formatPrice(discountPrice || displayPrice)}
                  </span>
                  {discountPrice && (
                    <span className="text-sm text-gray-500 line-through">
                      {formatPrice(displayPrice)}
                    </span>
                  )}
                </div>

                {/* Stock - Only show count if low stock */}
                {isLowStock && (
                  <div className="text-sm text-amber-600 font-medium">
                    Only {availableQuantity} left in stock
                  </div>
                )}
                {isOutOfStock && (
                  <div className="text-sm text-red-600 font-medium">
                    Out of stock
                  </div>
                )}
              </div>

              {/* Actions */}
              <AddToCartButton
                product={{
                  id: product.id,
                  title: product.title,
                  selling_price: product.selling_price, // This becomes 'price' in CartItem
                  sku: product.sku || '',
                  images: product.images
                }}
                variants={product.variants}
                availableQuantity={product.available_quantity}
                size="md"
                variant="primary"
                showText="desktop"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Grid View (Mobile First)
  return (
    <div
      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
      onClick={handleCardClick}
    >
      {/* Image Container */}
      <div className="relative aspect-square bg-gray-50">
        {primaryImage ? (
          <>
            {!imageLoaded && (
              <div className="absolute inset-0 bg-gray-200 animate-pulse" />
            )}
            <img
              src={primaryImage}
              alt={product.title}
              className={`w-full h-full object-cover ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
              onLoad={() => setImageLoaded(true)}
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/placeholder-product.png';
              }}
            />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="h-12 w-12 text-gray-300" />
          </div>
        )}

        {/* Top Badges */}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {product.is_featured && (
            <span className="px-2 py-1 bg-blue-600 text-white text-xs font-medium rounded">
              Featured
            </span>
          )}
          {product.discount_percent && (
            <span className="px-2 py-1 bg-red-600 text-white text-xs font-medium rounded">
              -{product.discount_percent}%
            </span>
          )}
        </div>

        {/* Stock Badge - Only show if low or out of stock */}
        {(isLowStock || isOutOfStock) && (
          <div className="absolute top-2 right-2">
            <span className={`px-2 py-1 text-xs font-medium rounded ${isOutOfStock
              ? 'bg-red-100 text-red-800'
              : 'bg-amber-100 text-amber-800'
              }`}>
              {isOutOfStock ? 'Out of stock' : `${availableQuantity} left`}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Title */}
        <h3 className="font-medium text-gray-900 mb-1 line-clamp-2 text-sm sm:text-base">
          {product.title}
        </h3>

        {/* Variant Indicator */}
        {hasVariants && (
          <div className="mb-2">
            <span className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-lg">
              {product.variants?.length || 0} options available
            </span>
          </div>
        )}

        {hasVariants && (
          <div className="mb-3">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-lg">
                {product.variants?.length || 0} variants
              </span>
              <span className="text-xs text-gray-500">Choose your preferred option</span>
            </div>
          </div>
        )}


        {/* Rating */}
        {product.rating && (
          <div className="flex items-center gap-1 mb-2">
            <Star className="h-3 w-3 sm:h-4 sm:w-4 text-amber-400 fill-current" />
            <span className="text-xs sm:text-sm text-gray-900">{product.rating}</span>
          </div>
        )}

        {/* Price and Actions Row */}
        <div className="flex items-center justify-between mt-3">
          <div>
            <div className="flex items-baseline gap-1 sm:gap-2">
              <span className="text-lg sm:text-xl font-bold text-gray-900">
                {formatPrice(discountPrice || displayPrice)}
              </span>
              {discountPrice && (
                <span className="text-xs sm:text-sm text-gray-500 line-through">
                  {formatPrice(displayPrice)}
                </span>
              )}
            </div>
          </div>

          {/* Add to Cart Button */}
          <button
            onClick={handleAddToCart}
            disabled={isOutOfStock || isAddingToCart || (hasMultipleVariants ? false : isOutOfStock)}
            className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1 transition-all ${isOutOfStock && !hasMultipleVariants
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : hasMultipleVariants
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white hover:scale-105'
                : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white hover:scale-105'
              }`}
          >
            {isAddingToCart ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <>
                <ShoppingCart className="h-4 w-4" />
                <span className="hidden xs:inline">
                  {hasMultipleVariants
                    ? 'View Options'
                    : isOutOfStock
                      ? 'Out of Stock'
                      : 'Add to Cart'
                  }
                </span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;